import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { ValidatorConfig, ValidatorRule, Condition, ConditionAtom, ConditionGroup } from '../api';
import { useAuth } from '../context/AuthContext';
import '../styles/validator-config.css';

const EMPTY_CONFIG: ValidatorConfig = {
  version: null,
  rules: []
};

const SEVERITY_OPTIONS: ValidatorRule['severity'][] = ['error', 'warning', 'info', 'critical'];

const OP_LABELS: Record<string, string> = {
  exists: 'exists',
  not_exists: 'does not exist',
  equals: 'equals',
  not_equals: 'does not equal',
  matches: 'matches regex',
  not_matches: 'does not match regex',
  contains: 'contains',
  starts_with: 'starts with',
  in: 'is one of',
  not_in: 'is not one of',
  typeof: 'typeof is'
};

function SeverityBadge({ severity }: { severity?: string }) {
  if (!severity) return null;
  return (
    <span className={`validator-config__severity validator-config__severity--${severity}`}>
      {severity}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span className={`validator-config__type-badge validator-config__type-badge--${type}`}>
      {type}
    </span>
  );
}

// --- Condition Editor Components ---

function ConditionAtomEditor({
  atom,
  onChange,
  onRemove
}: {
  atom: ConditionAtom;
  onChange: (a: ConditionAtom) => void;
  onRemove: () => void;
}) {
  return (
    <div className="validator-config__condition-atom">
      <div className="validator-config__condition-atom-fields">
        <input
          type="text"
          value={atom.target || ''}
          onChange={e => onChange({ ...atom, target: e.target.value })}
          placeholder="node.data.script"
          className="validator-config__mono"
        />
        <select
          value={atom.op}
          onChange={e => onChange({ ...atom, op: e.target.value as ConditionAtom['op'] })}
        >
          {Object.entries(OP_LABELS).map(([op, label]) => (
            <option key={op} value={op}>{label}</option>
          ))}
        </select>
        {!['exists', 'not_exists'].includes(atom.op) && (
          <input
            type="text"
            value={typeof atom.value === 'string' ? atom.value : JSON.stringify(atom.value ?? '')}
            onChange={e => onChange({ ...atom, value: e.target.value })}
            placeholder="value"
            className="validator-config__mono"
          />
        )}
        {['matches', 'not_matches'].includes(atom.op) && (
          <input
            type="text"
            value={atom.flags || ''}
            onChange={e => onChange({ ...atom, flags: e.target.value || undefined })}
            placeholder="flags"
            className="validator-config__mono"
            style={{ maxWidth: 60 }}
          />
        )}
      </div>
      <button className="validator-config__remove-btn validator-config__remove-btn--small" onClick={onRemove}>×</button>
    </div>
  );
}

function ConditionGroupEditor({
  group,
  onChange,
  onRemove,
  depth = 0
}: {
  group: ConditionGroup;
  onChange: (g: ConditionGroup) => void;
  onRemove?: () => void;
  depth?: number;
}) {
  const updateChild = (idx: number, child: Condition) => {
    const newConditions = [...group.conditions];
    newConditions[idx] = child;
    onChange({ ...group, conditions: newConditions });
  };

  const removeChild = (idx: number) => {
    onChange({ ...group, conditions: group.conditions.filter((_, i) => i !== idx) });
  };

  const addAtom = () => {
    const newAtom: ConditionAtom = { target: 'node.data.script', op: 'matches', value: '' };
    onChange({ ...group, conditions: [...group.conditions, newAtom] });
  };

  const addGroup = () => {
    const newGroup: ConditionGroup = { logic: 'and', conditions: [] };
    onChange({ ...group, conditions: [...group.conditions, newGroup] });
  };

  return (
    <div className={`validator-config__condition-group ${depth > 0 ? 'validator-config__condition-group--nested' : ''}`}>
      <div className="validator-config__condition-group-header">
        <select
          value={group.logic}
          onChange={e => onChange({ ...group, logic: e.target.value as 'and' | 'or' })}
          className="validator-config__logic-select"
        >
          <option value="and">ALL of (AND)</option>
          <option value="or">ANY of (OR)</option>
        </select>
        {onRemove && (
          <button className="validator-config__remove-btn validator-config__remove-btn--small" onClick={onRemove}>×</button>
        )}
      </div>
      {group.conditions.map((child, idx) => (
        <div key={idx}>
          {'logic' in child ? (
            <ConditionGroupEditor
              group={child as ConditionGroup}
              onChange={g => updateChild(idx, g)}
              onRemove={() => removeChild(idx)}
              depth={depth + 1}
            />
          ) : (
            <ConditionAtomEditor
              atom={child as ConditionAtom}
              onChange={a => updateChild(idx, a)}
              onRemove={() => removeChild(idx)}
            />
          )}
        </div>
      ))}
      <div className="validator-config__condition-actions">
        <button className="validator-config__add-condition-btn" onClick={addAtom}>+ Condition</button>
        {depth < 2 && (
          <button className="validator-config__add-condition-btn" onClick={addGroup}>+ Group</button>
        )}
      </div>
    </div>
  );
}

function ConditionEditor({
  condition,
  onChange
}: {
  condition?: Condition;
  onChange: (c: Condition) => void;
}) {
  // Ensure we have a root group
  const root: ConditionGroup = condition && 'logic' in condition
    ? condition as ConditionGroup
    : { logic: 'and', conditions: condition ? [condition as ConditionAtom] : [] };

  return (
    <ConditionGroupEditor group={root} onChange={onChange} />
  );
}

// --- Main Page ---

export function AdminValidatorConfigPage() {
  const { user } = useAuth();
  const [config, setConfig] = useState<ValidatorConfig>(EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  const canAccess = user?.role === 'admin';

  const fetchConfig = useCallback(async () => {
    try {
      const data = await api.getValidatorConfig();
      // Normalize: ensure rules array exists
      if (!data.rules) data.rules = [];
      setConfig(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canAccess) void fetchConfig();
    else { setLoading(false); setError('Admin privileges are required.'); }
  }, [canAccess, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await api.updateValidatorConfig(config);
      setSaveMsg(`Saved! Version: ${res.version}`);
      setConfig(prev => ({ ...prev, version: res.version }));
      setTimeout(() => setSaveMsg(null), 4000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    if (!confirm('This will replace the current config with all 28 bundled rules. Continue?')) return;
    setSeeding(true);
    try {
      const res = await api.seedValidatorConfig();
      setSaveMsg(`Seeded ${res.ruleCount} rules! Version: ${res.version}`);
      setTimeout(() => setSaveMsg(null), 4000);
      await fetchConfig();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSeeding(false);
    }
  };

  const toggleExpanded = (ruleId: string) => {
    setExpandedRules(prev => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });
  };

  // --- Rule helpers ---
  const rules = config.rules || [];

  const updateRule = (idx: number, patch: Partial<ValidatorRule>) => {
    setConfig(prev => ({
      ...prev,
      rules: (prev.rules || []).map((r, i) => i === idx ? { ...r, ...patch } : r)
    }));
  };

  const removeRule = (idx: number) => {
    setConfig(prev => ({
      ...prev,
      rules: (prev.rules || []).filter((_, i) => i !== idx)
    }));
  };


  const addRule = (type: ValidatorRule['type']) => {
    const newRule: ValidatorRule = {
      id: `${type}-${Date.now()}`,
      name: '',
      description: '',
      severity: 'warning',
      enabled: true,
      type
    };
    if (type === 'handler') {
      newRule.checkBody = '// check logic here\n';
    }
    if (type === 'condition') {
      newRule.conditions = { logic: 'and', conditions: [] };
      newRule.message = 'Node {nodeId} ("{nodeLabel}") matched a condition rule.';
    }
    if (type === 'pattern') {
      newRule.match = { target: 'node.data.script', pattern: '', flags: '', negate: false, nodeNameFilter: null };
      newRule.message = 'Node {nodeId} ("{nodeLabel}") matched a pattern rule.';
    }
    setConfig(prev => ({ ...prev, rules: [...(prev.rules || []), newRule] }));
    setExpandedRules(prev => new Set([...prev, newRule.id]));
  };

  if (!canAccess) {
    return <div className="validator-config"><p className="validator-config__error">Admin privileges are required.</p></div>;
  }

  if (loading) return <div className="validator-config"><p>Loading...</p></div>;

  const handlerRules = rules.filter(r => r.type === 'handler');
  const conditionRules = rules.filter(r => r.type === 'condition');
  const patternRules = rules.filter(r => r.type === 'pattern');

  return (
    <div className="validator-config">
      <div className="validator-config__header">
        <h1>Validator Config</h1>
        <p className="validator-config__subtitle">
          Manage validation rules for the IO Inspector extension.
          Changes take effect on next extension sync (up to 1 hour).
        </p>
        <div className="validator-config__header-meta">
          {config.version && (
            <span className="validator-config__version">Config version: {config.version}</span>
          )}
          <span className="validator-config__rule-count">{rules.length} rules total</span>
        </div>
      </div>

      {error && <div className="validator-config__error">{error} <button onClick={() => setError(null)}>×</button></div>}

      {/* Handler Rules (bundled) */}
      <section className="validator-config__section">
        <div className="validator-config__section-header">
          <h2>Handler Rules ({handlerRules.length})</h2>
          <p className="validator-config__hint">
            Rules with JavaScript check/fix logic managed remotely. The extension builds functions from checkBody/fixBody code strings.
          </p>
        </div>

        {handlerRules.length === 0 && (
          <div className="validator-config__cards-empty">
            <p>No handler rules configured.</p>
            <button className="button" onClick={handleSeed} disabled={seeding}>
              {seeding ? 'Seeding...' : 'Seed All Bundled Rules'}
            </button>
          </div>
        )}

        {handlerRules.map((rule) => {
          const idx = rules.indexOf(rule);
          return (
            <div key={rule.id} className={`validator-config__card ${!rule.enabled ? 'validator-config__card--disabled' : ''}`}>
              <div className="validator-config__card-header">
                <label className="validator-config__toggle">
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={e => updateRule(idx, { enabled: e.target.checked })}
                  />
                </label>
                <input
                  type="text"
                  value={rule.id}
                  onChange={e => updateRule(idx, { id: e.target.value })}
                  placeholder="rule-id"
                  className="validator-config__inline-input"
                />
                <span className="validator-config__rule-name">{rule.name}</span>
                <TypeBadge type={rule.type} />
                <SeverityBadge severity={rule.severity} />
                <button className="validator-config__remove-btn" onClick={() => removeRule(idx)}>Remove</button>
                <button
                  className="validator-config__expand-btn"
                  onClick={() => toggleExpanded(rule.id)}
                >
                  {expandedRules.has(rule.id) ? '▾' : '▸'}
                </button>
              </div>
              {expandedRules.has(rule.id) && (
                <div className="validator-config__card-fields">
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Name</span>
                    <input
                      type="text"
                      value={rule.name}
                      onChange={e => updateRule(idx, { name: e.target.value })}
                    />
                  </div>
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Severity</span>
                    <select
                      value={rule.severity}
                      onChange={e => updateRule(idx, { severity: e.target.value as ValidatorRule['severity'] })}
                    >
                      {SEVERITY_OPTIONS.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="validator-config__field validator-config__field--full">
                    <span className="validator-config__field-label">Description</span>
                    <input
                      type="text"
                      value={rule.description}
                      onChange={e => updateRule(idx, { description: e.target.value })}
                    />
                  </div>
                  <div className="validator-config__field validator-config__field--full">
                    <span className="validator-config__field-label">checkBody <span className="validator-config__field-hint">(required — function body receiving <code>ctx</code>)</span></span>
                    <textarea
                      className="validator-config__code-editor"
                      value={rule.checkBody || ''}
                      onChange={e => updateRule(idx, { checkBody: e.target.value })}
                      placeholder="// ctx.nodes, ctx.nodeIds, ctx.reachable, ctx.warn(), ctx.label(), ctx.helpers.*"
                      rows={8}
                      spellCheck={false}
                    />
                  </div>
                  <div className="validator-config__field validator-config__field--full">
                    <span className="validator-config__field-label">fixBody <span className="validator-config__field-hint">(optional — function body receiving <code>body, nodeId, helpers</code>)</span></span>
                    <textarea
                      className="validator-config__code-editor"
                      value={rule.fixBody || ''}
                      onChange={e => updateRule(idx, { fixBody: e.target.value || undefined })}
                      placeholder="// Return modified body, or null if no fix applied"
                      rows={6}
                      spellCheck={false}
                    />
                  </div>
                  <div className="validator-config__field validator-config__field--full">
                    <span className="validator-config__field-label">fixAllBody <span className="validator-config__field-hint">(optional — function body receiving <code>body, warnings, helpers</code>)</span></span>
                    <textarea
                      className="validator-config__code-editor"
                      value={rule.fixAllBody || ''}
                      onChange={e => updateRule(idx, { fixAllBody: e.target.value || undefined })}
                      placeholder="// Return modified body after fixing all warnings at once"
                      rows={4}
                      spellCheck={false}
                    />
                  </div>
                  <div className="validator-config__field validator-config__field--full">
                    <span className="validator-config__field-label">canFixBody <span className="validator-config__field-hint">(optional — function body receiving <code>warning</code>, returns boolean)</span></span>
                    <textarea
                      className="validator-config__code-editor"
                      value={rule.canFixBody || ''}
                      onChange={e => updateRule(idx, { canFixBody: e.target.value || undefined })}
                      placeholder="// Return true if this specific warning can be auto-fixed"
                      rows={2}
                      spellCheck={false}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <button className="button" onClick={() => addRule('handler')}>Add Handler Rule</button>
      </section>

      {/* Condition Rules */}
      <section className="validator-config__section">
        <div className="validator-config__section-header">
          <h2>Condition Rules ({conditionRules.length})</h2>
          <p className="validator-config__hint">
            Rules defined as JSON conditions — no extension code needed. Evaluated by the condition engine.
          </p>
        </div>

        {conditionRules.length === 0 && (
          <p className="validator-config__cards-empty">No condition rules configured.</p>
        )}

        {conditionRules.map((rule) => {
          const idx = rules.indexOf(rule);
          return (
            <div key={rule.id} className={`validator-config__card ${!rule.enabled ? 'validator-config__card--disabled' : ''}`}>
              <div className="validator-config__card-header">
                <label className="validator-config__toggle">
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={e => updateRule(idx, { enabled: e.target.checked })}
                  />
                </label>
                <input
                  type="text"
                  value={rule.id}
                  onChange={e => updateRule(idx, { id: e.target.value })}
                  placeholder="rule-id"
                  className="validator-config__inline-input"
                />
                <TypeBadge type={rule.type} />
                <SeverityBadge severity={rule.severity} />
                <button className="validator-config__remove-btn" onClick={() => removeRule(idx)}>Remove</button>
                <button
                  className="validator-config__expand-btn"
                  onClick={() => toggleExpanded(rule.id)}
                >
                  {expandedRules.has(rule.id) ? '▾' : '▸'}
                </button>
              </div>
              {expandedRules.has(rule.id) && (
                <div className="validator-config__card-fields">
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Name</span>
                    <input type="text" value={rule.name} onChange={e => updateRule(idx, { name: e.target.value })} placeholder="Rule display name" />
                  </div>
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Severity</span>
                    <select value={rule.severity} onChange={e => updateRule(idx, { severity: e.target.value as ValidatorRule['severity'] })}>
                      {SEVERITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="validator-config__field validator-config__field--full">
                    <span className="validator-config__field-label">Description</span>
                    <input type="text" value={rule.description} onChange={e => updateRule(idx, { description: e.target.value })} />
                  </div>
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Node name filter</span>
                    <input
                      type="text"
                      value={rule.nodeNameFilter || ''}
                      onChange={e => updateRule(idx, { nodeNameFilter: e.target.value || null })}
                      placeholder="e.g. fe_form (all if empty)"
                    />
                  </div>
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Node name pattern</span>
                    <input
                      type="text"
                      value={rule.nodeNamePattern || ''}
                      onChange={e => updateRule(idx, { nodeNamePattern: e.target.value || null })}
                      placeholder="Regex, e.g. ^script_"
                      className="validator-config__mono"
                    />
                  </div>
                  <div className="validator-config__field validator-config__field--full">
                    <span className="validator-config__field-label">Conditions</span>
                    <ConditionEditor
                      condition={rule.conditions}
                      onChange={c => updateRule(idx, { conditions: c })}
                    />
                  </div>
                  <div className="validator-config__field validator-config__field--full">
                    <span className="validator-config__field-label">Warning message</span>
                    <input
                      type="text"
                      value={rule.message || ''}
                      onChange={e => updateRule(idx, { message: e.target.value })}
                      placeholder="Use {nodeId} and {nodeLabel} as placeholders"
                    />
                    <span className="validator-config__field-hint">Available placeholders: {'{nodeId}'}, {'{nodeLabel}'}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <button className="button" onClick={() => addRule('condition')}>Add Condition Rule</button>
      </section>

      {/* Pattern Rules */}
      <section className="validator-config__section">
        <div className="validator-config__section-header">
          <h2>Pattern Rules ({patternRules.length})</h2>
          <p className="validator-config__hint">
            Regex-based rules that match against node fields. Evaluated alongside bundled and condition rules.
          </p>
        </div>

        {patternRules.length === 0 && (
          <p className="validator-config__cards-empty">No pattern rules configured.</p>
        )}

        {patternRules.map((rule) => {
          const idx = rules.indexOf(rule);
          return (
            <div key={rule.id} className={`validator-config__card ${!rule.enabled ? 'validator-config__card--disabled' : ''}`}>
              <div className="validator-config__card-header">
                <label className="validator-config__toggle">
                  <input
                    type="checkbox"
                    checked={rule.enabled}
                    onChange={e => updateRule(idx, { enabled: e.target.checked })}
                  />
                </label>
                <input
                  type="text"
                  value={rule.id}
                  onChange={e => updateRule(idx, { id: e.target.value })}
                  placeholder="rule-id"
                  className="validator-config__inline-input"
                />
                <TypeBadge type={rule.type} />
                <SeverityBadge severity={rule.severity} />
                <button className="validator-config__remove-btn" onClick={() => removeRule(idx)}>Remove</button>
                <button
                  className="validator-config__expand-btn"
                  onClick={() => toggleExpanded(rule.id)}
                >
                  {expandedRules.has(rule.id) ? '▾' : '▸'}
                </button>
              </div>
              {expandedRules.has(rule.id) && (
                <div className="validator-config__card-fields">
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Name</span>
                    <input type="text" value={rule.name} onChange={e => updateRule(idx, { name: e.target.value })} placeholder="Rule display name" />
                  </div>
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Severity</span>
                    <select value={rule.severity} onChange={e => updateRule(idx, { severity: e.target.value as ValidatorRule['severity'] })}>
                      {SEVERITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="validator-config__field validator-config__field--full">
                    <span className="validator-config__field-label">Description</span>
                    <input type="text" value={rule.description} onChange={e => updateRule(idx, { description: e.target.value })} placeholder="What this rule checks for" />
                  </div>
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Target field</span>
                    <select
                      value={rule.match?.target || 'node.data.script'}
                      onChange={e => updateRule(idx, { match: { ...rule.match!, target: e.target.value } })}
                    >
                      <option value="node.name">node.name</option>
                      <option value="node.data.script">node.data.script</option>
                      <option value="node.data.html">node.data.html</option>
                      <option value="node.data.body">node.data.body</option>
                      <option value="node.data.url">node.data.url</option>
                      <option value="node.data.template">node.data.template</option>
                      <option value="node.data.head">node.data.head</option>
                      <option value="node.meta.title">node.meta.title</option>
                    </select>
                  </div>
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Node name filter</span>
                    <input
                      type="text"
                      value={rule.match?.nodeNameFilter || ''}
                      onChange={e => updateRule(idx, { match: { ...rule.match!, nodeNameFilter: e.target.value || null } })}
                      placeholder="e.g. fe_form (all if empty)"
                    />
                  </div>
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Regex pattern</span>
                    <input
                      type="text"
                      value={rule.match?.pattern || ''}
                      onChange={e => updateRule(idx, { match: { ...rule.match!, pattern: e.target.value } })}
                      placeholder="e.g. ^(test_|debug_)"
                      className="validator-config__mono"
                    />
                  </div>
                  <div className="validator-config__field">
                    <span className="validator-config__field-label">Flags</span>
                    <input
                      type="text"
                      value={rule.match?.flags || ''}
                      onChange={e => updateRule(idx, { match: { ...rule.match!, flags: e.target.value } })}
                      placeholder="e.g. i, gi"
                      className="validator-config__mono"
                      style={{ maxWidth: 100 }}
                    />
                  </div>
                  <div className="validator-config__field">
                    <label className="validator-config__toggle">
                      <input
                        type="checkbox"
                        checked={!!rule.match?.negate}
                        onChange={e => updateRule(idx, { match: { ...rule.match!, negate: e.target.checked } })}
                      />
                      Negate (warn when pattern does NOT match)
                    </label>
                  </div>
                  <div className="validator-config__field validator-config__field--full">
                    <span className="validator-config__field-label">Warning message</span>
                    <input
                      type="text"
                      value={rule.message || ''}
                      onChange={e => updateRule(idx, { message: e.target.value })}
                      placeholder="Use {nodeId} and {nodeLabel} as placeholders"
                    />
                    <span className="validator-config__field-hint">Available placeholders: {'{nodeId}'}, {'{nodeLabel}'}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <button className="button" onClick={() => addRule('pattern')}>Add Pattern Rule</button>
      </section>

      {/* Save */}
      <div className="validator-config__actions">
        <button className="button" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Config'}
        </button>
        <button className="button validator-config__seed-btn" onClick={handleSeed} disabled={seeding}>
          {seeding ? 'Seeding...' : 'Re-seed All Rules'}
        </button>
        {saveMsg && <span className="validator-config__save-msg">{saveMsg}</span>}
      </div>
    </div>
  );
}
